import type { FC, ReactNode } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import { emailMustBeValid, emailMustHaveAtSign } from "../../helpers/password-errors";

import { EulaError, serverLogin } from "api/api-server-actions";
import { t } from "i18n/i18n";
import { FormField } from "mds/components/FormField";
import { TextField } from "mds/components/TextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { LoadingDotsIcon } from "mds/icons";
import { useNavigate } from "utils/routing";

interface EmailLoginFormValue {
  email: string;
  password: string;
}

interface EmailLoginFormProps {
  onLoggedIn: () => void;
  children?: ReactNode | ReactNode[];
}

export const EmailLoginForm: FC<EmailLoginFormProps> = ({ onLoggedIn, children }) => {
  const form = useForm<EmailLoginFormValue>();
  const navigate = useNavigate();

  const handleSubmit: SubmitHandler<EmailLoginFormValue> = (formData) =>
    serverLogin(formData.email, formData.password)
      .then(() => {
        onLoggedIn();
      })
      .catch((error) => {
        if (
          error instanceof Error &&
          error.name === "EULA_NOT_ACCEPTED" &&
          (error as EulaError).token &&
          (error as EulaError).orgId
        ) {
          // Redirect to onboarding with token and org_id
          const eulaError = error as EulaError;
          navigate(`/onboarding-invite?token=${eulaError.token}&org_id=${eulaError.orgId}`);
        } else if (error instanceof Error && error.name === "EULA_NOT_ACCEPTED") {
          form.setError(
            "email",
            {
              type: "server",
              message: t("error.org_not_found"),
            },
            { shouldFocus: true },
          );
        } else {
          form.setError(
            "password",
            {
              type: "server",
              message: t("error.incorrect_email_or_password"),
            },
            { shouldFocus: true },
          );
        }
      });

  return (
    <FormProvider {...form}>
      <form
        className="mb-3 flex w-full flex-col gap-3"
        noValidate
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormField id="email" label={t("user_info.email")} name="email" required>
          {(controlProps) => (
            <TextField
              {...form.register("email", {
                required: t("error.field_required"),
                validate: {
                  emailMustHaveAtSign,
                  emailMustBeValid,
                },
              })}
              {...controlProps}
              autoComplete="email"
              eventName="Account - Sign in / Email"
              eventPropertyKey="userEmail"
              type="email"
              required
            />
          )}
        </FormField>

        <FormField id="password" label={t("user_info.password")} name="password" required>
          {(controlProps) => (
            <TextField
              {...form.register("password", {
                required: t("error.field_required"),
              })}
              {...controlProps}
              autoComplete="current-password"
              eventName="Account - Sign in / Password"
              type="password"
              required
            />
          )}
        </FormField>

        <div className="mt-3 flex items-center gap-2">
          <SquareButton
            eventName="Account - Sign in / Confirm"
            kind="accent"
            size="m"
            type="submit"
            value="Login"
          >
            {t("user_info.sign_in")}
          </SquareButton>
          {form.formState.isSubmitting && <LoadingDotsIcon aria-label={t("common.submitting")} />}
        </div>

        {children}
      </form>
    </FormProvider>
  );
};
